import { useCallback, useEffect, useState, useRef } from "react";
import {
	VIDEO_ELEMENT,
	REFLECTION_ELEMENT,
	SURVEY_ELEMENT,
	LINK_ELEMENT,
	IMAGE_ELEMENT,
	AUDIO_ELEMENT,
	DOWNLOAD_ELEMENT,
	TEXT_ELEMENT,
	DOCUMENT_ELEMENT,
	SIGNATURE_ELEMENT,
	ROLE_STUDENT,
	RATING_ELEMENT,
	QUOTES_ELEMENT,
	CUSTOM_PLAN_ELEMENT,
	ROLE_EMPLOYEE,
	LIBRARY_TYPE_TRAINING,
	LIBRARY_TYPE_IN_PERSON_TRAINING,
	LIBRARY_TYPE_WEBINAR,
	LIBRARY_TYPE_INTELLIGUIDE,
	LIBRARY_TYPE_LEARNING_HABIT,
	SIDEBAR_LIBRARY_LESSON_DETAILS_PAGE,
} from "../models/Constants";

import { ROLE_ADMIN, ROLE_MENTOR, ROLE_OWNER } from "../models/Constants";
import { isEmpty } from "./Functions";
import { useLocation } from "react-router-dom";
import { useMatch, useNavigate } from "react-router-dom";
import {
	URL_LIBRARY_IN_PERSON_TRAINING,
	URL_LIBRARY_INTELLIGUIDE,
	URL_LIBRARY_LEARNING_HABITS,
	URL_LIBRARY_TRAINING,
	URL_LIBRARY_WEBINARS,
} from "../app/dashboard/DashboardRoutes";

export const getRole = (roleID) => {
	switch (roleID) {
		case 1:
			return "Admin";
		case 2:
			return "Student";
		case 3:
			return "Mentor";
		case 4:
			return "Super Admin";
		case 5:
			return "Employee";
		case 6:
			return "Owner";
		default:
			return "Custom";
	}
};

export const roleNameToStandardRoleId = (roleName) => {
	switch (roleName?.toLowerCase()) {
		case "student":
			return ROLE_STUDENT;
		case "admin":
			return ROLE_ADMIN;
		case "mentor":
			return ROLE_MENTOR;
		case "owner":
			return ROLE_OWNER;
		case "employee":
			return ROLE_EMPLOYEE;
		default:
			return null;
	}
};

export const getCompanyCapabilities = (userCapabilities, companyID) => {
	let selected;

	for (const obj of userCapabilities) {
		if (obj.company_id === companyID) {
			selected = obj;
			break;
		}
	}

	return selected;
};

export const getElementType = (elementTypeID) => {
	switch (elementTypeID) {
		case VIDEO_ELEMENT:
			return "Video";
		case REFLECTION_ELEMENT:
			return "Reflection";
		case SURVEY_ELEMENT:
			return "Survey";
		case LINK_ELEMENT:
			return "Link";
		case IMAGE_ELEMENT:
			return "Image";
		case AUDIO_ELEMENT:
			return "Audio";
		case DOWNLOAD_ELEMENT:
			return "Download";
		case TEXT_ELEMENT:
			return "Text";
		case DOCUMENT_ELEMENT:
			return "Document";
		case SIGNATURE_ELEMENT:
			return "Signature";
		case RATING_ELEMENT:
			return "Ratings";
		case QUOTES_ELEMENT:
			return "Quote";
		case CUSTOM_PLAN_ELEMENT:
			return "Custom Plan";
	}
};

export const isNotStudent = (role) => {
	if (isEmpty(role)) {
		return false;
	}

	return role != ROLE_STUDENT;
};

export const useLoading = () => {
	const [loadingCount, setLoadingCount] = useState(0);
	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		setIsLoading(loadingCount > 0);
	}, [loadingCount]);

	const addLoading = () => setLoadingCount((prev) => prev + 1);
	const finishLoading = () => setLoadingCount((prev) => Math.max(0, prev - 1));

	return {
		isLoading,
		setIsLoading,
		loadingCount,
		addLoading,
		finishLoading,
	};
};

export const useMultiLoading = () => {
	const [loadingCounts, setLoadingCounts] = useState({});
	const [globalLoading, setGlobalLoading] = useState(false);

	useEffect(() => {
		// Global loading state should be true if any key has a count > 0
		setGlobalLoading(Object.values(loadingCounts).some((count) => count > 0));
	}, [loadingCounts]);

	const addLoading = (key = null) => {
		setLoadingCounts((prev) => {
			if (key) {
				return { ...prev, [key]: (prev[key] || 0) + 1 };
			}
			return { ...prev, global: (prev.global || 0) + 1 };
		});
	};

	const finishLoading = (key = "global", weight = 1) => {
		setLoadingCounts((prev) => {
			if (key) {
				if (weight < 0) {
					// wipe all loading count
					const updatedState = { ...prev };
					delete updatedState[key];
					return updatedState;
				}

				return { ...prev, [key]: Math.max(0, (prev[key] || 0) - weight) };
			}
		});
	};

	const isLoading = (key = null) => {
		if (key) {
			if (!loadingCounts[key]) {
				return false;
			}

			return (loadingCounts[key] || 0) > 0;
		}
		return globalLoading;
	};

	return {
		isLoading,
		addLoading,
		finishLoading,
		loadingCounts, // Useful for debugging if needed
	};
};

export const useForceRender = () => {
	const [, setToggle] = useState(false);

	// Function to force a render
	const forceRender = useCallback(() => {
		setToggle((prev) => !prev);
	}, []);

	return forceRender;
};

export const useFirstMount = () => {
	const isFirstMount = useRef(true);

	useEffect(() => {
		isFirstMount.current = false;
	}, []);

	return isFirstMount.current;
};

export const useCompanyUrl = () => {
	const [isTellix, setIsTellix] = useState(false);

	const location = useLocation();
	let baseUrl = process.env.REACT_APP_TELLIX_URL ?? "";

	if (!isEmpty(baseUrl)) {
		baseUrl = new URL(baseUrl).origin;
	}

	useEffect(() => {
		setIsTellix(window.location.origin === baseUrl);
	}, [location]);

	return {
		isTellix,
	};
};

export const convertLibraryType = (value) => {
	const mapping = {
		[LIBRARY_TYPE_TRAINING]: URL_LIBRARY_TRAINING,
		[LIBRARY_TYPE_IN_PERSON_TRAINING]: URL_LIBRARY_IN_PERSON_TRAINING,
		[LIBRARY_TYPE_WEBINAR]: URL_LIBRARY_WEBINARS,
		[LIBRARY_TYPE_INTELLIGUIDE]: URL_LIBRARY_INTELLIGUIDE,
		[LIBRARY_TYPE_LEARNING_HABIT]: URL_LIBRARY_LEARNING_HABITS,
	};

	// everything that is a number is a library type and be converted to a library type name/url
	let toBaseUrl = !Number.isNaN(parseInt(value));

	if (toBaseUrl) {
		return mapping[value] || null;
	}

	const reverseMapping = Object.fromEntries(Object.entries(mapping).map(([k, v]) => [v, k]));

	return parseInt(reverseMapping[value]) || null;
};

export const useLibraryRoutesUtils = () => {
	const libraryPageLocation = useMatch("/dashboard/:type/:library_code");
	const navigate = useNavigate();

	const libraryType = libraryPageLocation?.params?.type;
	const libraryCode = libraryPageLocation?.params?.library_code;

	const gotoLibrary = (libraryCode, libraryType = null) => {
		let libraryBaseTypeUrl;

		if (!isEmpty(libraryType)) {
			libraryBaseTypeUrl = convertLibraryType(libraryType);
		}
		// if no library type is provided and the user is already on a valid library dashboard route, use the current route's base type.
		else if (!isEmpty(libraryType)) {
			libraryBaseTypeUrl = libraryType;
		}

		if (!isEmpty(libraryBaseTypeUrl)) {
			const isLearningHabitBaseUrl = libraryBaseTypeUrl === URL_LIBRARY_LEARNING_HABITS;

			if (isLearningHabitBaseUrl) {
				libraryBaseTypeUrl = libraryBaseTypeUrl + "/lessons";
			}

			let libraryUrl = `/dashboard/${libraryBaseTypeUrl}/${libraryCode}`;

			if (isLearningHabitBaseUrl) {
				libraryUrl += `#${SIDEBAR_LIBRARY_LESSON_DETAILS_PAGE}`; // direcly go to lesson page
			}

			navigate(libraryUrl, { replace: true });
			return true;
		}

		return false;
	};

	return {
		libraryTpeBaseUrl: libraryType,
		libraryTypeId: convertLibraryType(libraryType),
		libraryCode,
		gotoLibrary,
	};
};

/**
 * Truncate text to a word limit, adding "..." if exceeded.
 * Handles long single words by truncating them as well.
 * @param {string} text - The input text to truncate.
 * @param {number} wordLimit - Maximum number of words allowed.
 * @param {number} [maxWordLength=30] - Maximum allowed length for a single word before truncating it.
 * @returns {string} - Truncated text with ellipsis if needed.
 */
export function truncateTextWithEllipsis(text, wordLimit, maxWordLength = 30) {
	if (!text || typeof text !== "string" || wordLimit <= 0) return "";

	const words = text.split(/\s+/);

	// Handle long single word spam
	const processedWords = words.map((word) =>
		word.length > maxWordLength ? word.slice(0, maxWordLength) + "..." : word
	);

	if (processedWords.length <= wordLimit) {
		return processedWords.join(" ");
	}

	const truncated = processedWords.slice(0, wordLimit).join(" ");
	return truncated + "...";
}
